import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Layer,
  Spinner,
  Notification,
  Select,
} from "grommet";
import { Add } from "grommet-icons";
import { useParams } from "react-router-dom";
import axios from "axios";
import { HeaderNavigation } from "../../components/Headers/AdminHeader";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import CourseCard from "../../components/AdminPortal/CourseCard";
import AddCourseModal from "../../components/AdminPortal/AddCourseModal";

const ViewCourses = () => {
  const { portfolio_id, learning_path_id } = useParams();
  const [courses, setCourses] = useState([]);
  const [initialCourses, setInitialCourses] = useState([]);
  const [pathName, setPathName] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastStatus, setToastStatus] = useState("normal");
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseSwaps, setCourseSwaps] = useState([]); // To track the swapped course pairs
  const [confirmData, setConfirmData] = useState([]); // holds warnings
  const [showConfirm, setShowConfirm] = useState(false); // show confirmation modal
  const [pendingPayload, setPendingPayload] = useState(null); // holds payload until confirmed
  const listRef = useRef();

  // 1️⃣ Load current courses & stash originals
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `/api/admin/learning-path/course/${learning_path_id}`,
        );
        const norm = (data.courses || []).map((c) => ({
          course_id: c.course_id ?? c.id,
          courseName: c.name ?? "N/A",
          c_id: c.c_id ?? "N/A",
          duration: c.duration ?? "N/A",
          OEM: c.OEM ?? "N/A",
          isElearning: c.isElearning ?? -1,
          level: c.level ?? -1,
          status: c.status ?? 0,
          course_order: c.course_order,
          originalOrder: c.course_order,
          originalStatus: c.status,
        }));
        // .sort((a, b) => {
        //   const oa = a.course_id == null ? Infinity : a.course_id;
        //   const ob = b.course_id == null ? Infinity : b.course_id;
        //   return oa - ob;
        // });
        setCourses(norm);
        setInitialCourses(norm);
        setPathName(data.learning_path_name ?? "N/A");
      } catch (err) {
        console.error("Error loading courses:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [learning_path_id]);

  // 2️⃣ Auto-scroll on list growth
  useEffect(() => {
    if (!loading && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [courses, loading]);
  useEffect(() => {
    if (!showAddExisting) return;
    axios
      .get(
        `/api/admin/get-unmapped-courses/${portfolio_id}/${learning_path_id}`,
      )
      .then(({ data }) => {
        const existingIds = new Set(courses.map((c) => c.course_id));
        setAllCourses(
          (data.courses || []).filter((c) => !existingIds.has(c.id)),
        );
      })
      .catch((err) => console.error("Fetch unmapped failed:", err));
  }, [showAddExisting, portfolio_id, learning_path_id, courses]);

  // 3️⃣ Move & toggle handlers
  const handleMoveUp = (idx) => {
    if (idx === 0) return;

    // Swap courses and track the swap
    setCourses((prev) => {
      const cp = [...prev];
      [cp[idx - 1], cp[idx]] = [cp[idx], cp[idx - 1]];

      // Track the swapped course ids
      setCourseSwaps((prevSwaps) => [
        ...prevSwaps,
        { course_id_1: cp[idx].course_id, course_id_2: cp[idx - 1].course_id },
      ]);

      return cp.map((c, i) => ({ ...c, course_order: i + 1 }));
    });
  };

  const handleMoveDown = (idx) => {
    if (idx === courses.length - 1) return;

    // Swap courses and track the swap
    setCourses((prev) => {
      const cp = [...prev];
      [cp[idx], cp[idx + 1]] = [cp[idx + 1], cp[idx]];

      // Track the swapped course ids
      setCourseSwaps((prevSwaps) => [
        ...prevSwaps,
        { course_id_1: cp[idx].course_id, course_id_2: cp[idx + 1].course_id },
      ]);

      return cp.map((c, i) => ({ ...c, course_order: i + 1 }));
    });
  };
  const handleStatusChange = (idx) => {
    // Toggle the status between 0 (Unpublished) and 1 (Published)
    setCourses((prev) =>
      prev.map((course, i) =>
        i === idx
          ? { ...course, status: course.status === 1 ? 0 : 1 } // Toggle status
          : course,
      ),
    );
  };

  const hasStatusChanged = (original, updated) => {
    // Create a map for the first dataset using course_id as the key
    const origStatusMap = original.reduce((acc, c) => {
      acc[c.course_id] = c.status;
      return acc;
    }, {});

    // Set of all original IDs
    const origIds = new Set(original.map((c) => c.course_id));

    // Walk through the updated list
    for (let course of updated) {
      // 1️⃣ New course detected
      if (!origIds.has(course.course_id)) {
        return true;
      }
      // 2️⃣ Status changed
      if (origStatusMap[course.course_id] !== course.status) {
        return true;
      }
    }

    // No new courses and no status diffs
    return false;
  };

  // 5️⃣ Detect diffs vs original
  useEffect(() => {
    const diff = courses.some(
      (c) =>
        c.originalOrder !== c.course_order || c.originalStatus !== c.status,
    );
    setHasChanges(diff);
  }, [courses]);

  // 7️⃣ Save → only check courses that actually changed
  const handleSave = async () => {
    // Log original data (before changes)

    // If there are no swaps, return early
    // if (
    //   courseSwaps.length === 0 &&
    //   !hasStatusChanged(courses, initialCourses)
    // ) {
    //   // return;
    // }

    // Log updated data (after changes)

    const payload = {
      learningPathId: learning_path_id,
      courses: courses.map((c) => ({
        course_id: c.course_id,
        course_order: c.course_order,
        status: c.status,
      })),
      ...(courseSwaps.length !== 0 && { course_swaps: courseSwaps }), // Add course_swaps only if it's not empty
    };

    setPendingPayload(payload);

    try {
      // Call the API with the swaps payload
      const { data } = await axios.patch(
        `/api/admin/learning-path/${learning_path_id}/update-courses`,
        payload,
      );
      if (data && courseSwaps.length !== 0) {
        const swapResponse = await axios.patch(
          `/api/admin/learning-path/${learning_path_id}/swap-courses`,
          payload, // This contains the data to be updated
        );
        if (swapResponse.status !== 200) {
          throw new Error("Failed to update courses");
        }
      }

      setToastMessage(data.message || "Changes saved successfully");
      setToastStatus("normal");
      setShowToast(true);
      setHasChanges(false);
      setInitialCourses(courses);
      setTimeout(() => {
        setShowToast(false);
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error("Error saving changes:", err);
      const msg = err.response?.data?.error || "Failed to save changes";
      setToastMessage(msg);
      setToastStatus("critical");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
  };
  const handleAddExisting = () => {
    const toAdd = allCourses
      .filter((c) => selectedExisting.includes(c.id)) // Filter the selected courses
      .map((c, i) => ({
        course_id: c.id,
        courseName: c.name ?? "N/A",
        c_id: c.c_id ?? "N/A",
        duration: c.duration ?? "N/A",
        OEM: c.OEM ?? "N/A",
        isElearning: c.isElearning ?? -1,
        level: c.level ?? -1,
        status: 0, // Default to unpublished
        course_order: courses.length + i + 1, // Set order based on existing length
        originalOrder: null,
        originalStatus: c.status,
      }));

    // Add the selected courses to the existing courses
    setCourses((prev) => [...prev, ...toAdd]);

    // Close the modal and reset the selected courses
    setShowAddExisting(false);
    setSelectedExisting([]);
  };
  const confirmSubmit = async () => {
    setShowConfirm(false); // Close the confirmation modal

    try {
      // Send the changes to the backend for final submission
      const { data } = await axios.patch(
        `/api/admin/learning-path/${learning_path_id}/update-courses`,
        pendingPayload, // This contains the data to be updated
      );
      await axios.patch(
        `/api/admin/learning-path/${learning_path_id}/swap-courses`,
        pendingPayload,
      );

      // Display success message
      setToastMessage(data.message || "Changes saved successfully");
      setToastStatus("normal");
      setShowToast(true);
      setHasChanges(false);
      setInitialCourses(courses); // Reset initial courses to reflect the saved changes

      // Reload the page after a short delay to reflect the updates
      setTimeout(() => {
        setShowToast(false);
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error("Final submit error:", err);
      const msg = err.response?.data?.error || "Failed to save changes";
      setToastMessage(msg);
      setToastStatus("critical");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        window.location.reload();
      }, 2000);
    }
  };

  return (
    <Box fill="vertical" justify="between">
      <Box flex="grow">
        <Box>
          {showToast && (
            <Notification
              toast
              status={toastStatus}
              message={toastMessage}
              onClose={() => setShowToast(false)}
            />
          )}
          <HeaderNavigation />
          <BreadcrumbNavigation
            breadcrumbs={[
              {
                label: "Manage Learning Paths",
                link: `/admin/portfolio/${portfolio_id}/learning-paths`,
              },
              {
                label: "View Courses",
                link: `/admin/portfolio/${portfolio_id}/learning-path/${learning_path_id}/courses`,
              },
            ]}
          />
          <Box height="2px" background="brand" margin={{ vertical: "small" }} />

          <Box
            flex
            pad="small"
            ref={listRef}
            // style={{ maxHeight: "500px" }}
          >
            {loading ? (
              <Box fill align="center" justify="center" gap="small">
                <Spinner size="large" />
                <Text>Loading Courses…</Text>
              </Box>
            ) : (
              <>
                <Box
                  pad="small"
                  direction="row"
                  align="center"
                  justify="between"
                >
                  <Box>
                    <Heading level={2} margin="none">
                      {pathName} – Learning Path Courses
                    </Heading>
                    <Text>{courses.length} course(s) added</Text>
                  </Box>
                  <Box direction="row" gap="small" align="center">
                    <Button
                      label="Save Changes"
                      onClick={handleSave}
                      primary={hasChanges}
                      secondary={!hasChanges}
                      disabled={!hasChanges}
                    />
                    <Button
                      icon={<Add />}
                      label="Add Existing Course"
                      secondary
                      onClick={() => setShowAddExisting(true)}
                    />
                    <Button
                      icon={<Add />}
                      label="Add New Course"
                      primary
                      onClick={() => setShowAddNew(true)}
                    />
                  </Box>
                </Box>
                <Box flex pad="small" gap="small" overflow="auto">
                  {courses.map((c, i) => (
                    <CourseCard
                      key={`${learning_path_id}-${c.course_id}`}
                      course={c}
                      index={i}
                      total={courses.length}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                      onStatusChange={() => handleStatusChange(i)}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
          {showAddNew && (
            <Layer
              onEsc={() => setShowAddNew(false)}
              onClickOutside={() => setShowAddNew(false)}
            >
              <AddCourseModal
                portfolio_id={portfolio_id}
                onClose={() => setShowAddNew(false)}
                onSubmit={(courseData) => {
                  if (
                    courses.some((c) => c.courseName === courseData.courseName)
                  ) {
                    setShowAddNew(false);
                    return;
                  }
                  const newC = {
                    course_id: courseData.courseCode,
                    courseName: courseData.courseName,
                    c_id: courseData.courseCode,
                    duration:
                      courseData.courseDuration + " " + courseData.durationUnit,
                    OEM: courseData.oem,
                    isElearning:
                      courseData.courseMode === "ILT"
                        ? 0
                        : courseData.courseMode === "VILT"
                          ? 1
                          : 2,
                    level: parseInt(courseData.courseLevel, 10) || -1,
                    status: 0,
                    course_order: courses.length + 1,
                    originalOrder: null,
                    originalStatus: 0,
                  };
                  setCourses((prev) => [...prev, newC]);
                  setShowAddNew(false);
                }}
              />
            </Layer>
          )}

          {showAddExisting && (
            <Layer
              onEsc={() => setShowAddExisting(false)}
              onClickOutside={() => setShowAddExisting(false)}
            >
              <Box pad="medium" gap="medium" width="medium">
                <Heading level={3} margin="none">
                  Add Existing Courses
                </Heading>
                <Select
                  multiple
                  options={allCourses}
                  labelKey={(option) => `${option.c_id} - ${option.name}`}
                  valueKey={{ key: "id", reduce: true }}
                  placeholder="Select existing courses"
                  value={selectedExisting}
                  onChange={({ value }) => setSelectedExisting(value)}
                  dropHeight="small"
                  closeOnChange
                />
                {selectedExisting.length > 0 && (
                  <Text>
                    <Text weight="bold">Selected:</Text>{" "}
                    {allCourses
                      .filter((c) => selectedExisting.includes(c.id))
                      .map((c) => c.name)
                      .join(", ")}
                  </Text>
                )}
                <Box
                  direction="row"
                  gap="small"
                  justify="end"
                  margin={{ top: "medium" }}
                >
                  <Button
                    label="Cancel"
                    onClick={() => setShowAddExisting(false)}
                  />
                  <Button
                    label="Add Selected"
                    primary
                    disabled={!selectedExisting.length}
                    onClick={handleAddExisting}
                  />
                </Box>
              </Box>
            </Layer>
          )}

          {/* Confirmation modal for multiple‐learning‐paths */}
          {showConfirm && (
            <Layer
              onEsc={() => setShowConfirm(false)}
              onClickOutside={() => setShowConfirm(false)}
            >
              <Box pad="medium" gap="small" width="large">
                <Heading level={3} margin="none">
                  Warning: Courses in multiple paths
                </Heading>
                <Text>
                  The following course(s) are already in other learning paths,
                  changing their order or status may affect those paths as well:
                </Text>
                <Box pad={{ vertical: "small" }} gap="small">
                  {confirmData.map((w) => (
                    <Box
                      key={w.courseId}
                      border={{ color: "light-4" }}
                      pad="small"
                    >
                      <Text weight="bold">{w.courseName}</Text>
                      <Text size="small">
                        Present in {w.learningPathCount} paths:
                      </Text>
                      <Box pad={{ left: "small" }} gap="xsmall">
                        {w.learningPaths.map((lp) => (
                          <Text key={lp.learningPathId} size="small">
                            • {lp.learningPathName}
                          </Text>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box direction="row" justify="end" gap="small">
                  <Button
                    label="Cancel"
                    onClick={() => setShowConfirm(false)}
                  />
                  <Button
                    label="Yes, continue"
                    primary
                    onClick={confirmSubmit}
                  />
                </Box>
              </Box>
            </Layer>
          )}
        </Box>
      </Box>
      <StickyFooter />
    </Box>
  );
};

export default ViewCourses;
